import React, { useEffect, useState, useMemo } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FileText, CheckCircle2, Clock, Mail, Download, Filter, Banknote, CreditCard, Search, Plus, Trash2, Loader2, Bell } from "lucide-react";
import { toast } from "sonner";
import { RowBin, ClearArea } from "@/components/admin/Purge";

const API = `${BACKEND_URL}/api`;
const VAT_RATE = 0.20;

// iter90p — recoloured: brand-blue replaces the amber "warning sticker" wash.
// Calm enterprise palette: white/slate base, #0055FF brand accent on Awaiting/issued states.
const STATUS_COLORS = {
  issued:   "bg-[#0055FF]/10 text-[#0055FF] border-[#0055FF]/20",
  paid:     "bg-emerald-50 text-emerald-700 border-emerald-200",
  overdue:  "bg-rose-50 text-rose-700 border-rose-200",
  refunded: "bg-slate-50 text-slate-600 border-slate-200",
  open:     "bg-[#0055FF]/10 text-[#0055FF] border-[#0055FF]/20",
};

export default function AdminInvoices() {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [pmFilter, setPmFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  // iter90p — vault-wide stats (independent of filters) + Nudge-all dry-run flow
  const [vaultStats, setVaultStats] = useState({ total: 0, paid: 0, unpaid: 0, stale_unpaid: 0, recently_issued: 0, outstanding_gbp: 0, paid_gbp: 0 });
  const [nudgePreview, setNudgePreview] = useState(null);   // { would_nudge, would_skip, preview[], ... } | null
  const [nudgePreviewBusy, setNudgePreviewBusy] = useState(false);
  const [nudgeSending, setNudgeSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (pmFilter !== "all") params.payment_method = pmFilter;
      const [list, stats] = await Promise.all([
        api.get("/admin/invoices", { params }),
        api.get("/admin/invoices/stats"),
      ]);
      setRows(list.data || []);
      setVaultStats(stats.data || {});
    } catch (e) {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, pmFilter]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const s = search.toLowerCase();
    return rows.filter(r =>
      (r.number || "").toLowerCase().includes(s) ||
      (r.buyer?.company || "").toLowerCase().includes(s) ||
      (r.buyer?.email || "").toLowerCase().includes(s)
    );
  }, [rows, search]);

  // (iter90p removed the local kpis useMemo — vaultStats now drives the KPI strip)

  const handleAuthError = (status, detail) => {
    if (status === 401) {
      toast.error("Your session expired — please sign in again.");
      setTimeout(() => { window.location.href = "/login?next=/admin/invoices"; }, 1500);
      return true;
    }
    return false;
  };

  const markPaid = async (iid) => {
    try {
      await api.post(`/admin/invoices/${iid}/mark-paid`);
      toast.success("Marked paid");
      await load();
      if (active?.id === iid) setActive(null);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (handleAuthError(status, detail)) return;
      toast.error(typeof detail === "string" ? detail : "Failed to mark paid");
    }
  };

  const resend = async (iid) => {
    try {
      const { data } = await api.post(`/admin/invoices/${iid}/resend`);
      toast.success(data.ok ? "Invoice resent" : "Email queued (preview only)");
    } catch (e) {
      const status = e?.response?.status;
      if (handleAuthError(status)) return;
      toast.error("Failed to resend");
    }
  };

  // iter90p — open the dry-run preview dialog (no emails sent yet)
  const openNudgePreview = async () => {
    setNudgePreviewBusy(true);
    try {
      const { data } = await api.post("/admin/invoices/nudge-all-stale?dry_run=true");
      setNudgePreview(data);
    } catch (e) {
      const status = e?.response?.status;
      if (handleAuthError(status)) return;
      toast.error("Could not load nudge preview");
    } finally {
      setNudgePreviewBusy(false);
    }
  };

  // iter90p — actually fire the bulk nudge after the admin reviews the preview
  const confirmNudgeAll = async () => {
    setNudgeSending(true);
    try {
      const { data } = await api.post("/admin/invoices/nudge-all-stale");
      const bits = [];
      if (data.nudged)  bits.push(`${data.nudged} nudged`);
      if (data.skipped) bits.push(`${data.skipped} skipped (recently nudged)`);
      if (data.failed)  bits.push(`${data.failed} failed`);
      const msg = bits.join(" · ") || "Nothing to nudge.";
      (data.failed && !data.nudged) ? toast.error(msg) : toast.success(msg);
      setNudgePreview(null);
      await load();
    } catch (e) {
      const status = e?.response?.status;
      if (handleAuthError(status)) return;
      toast.error("Could not nudge invoices");
    } finally {
      setNudgeSending(false);
    }
  };

  const downloadPdf = async (iid) => {
    try {
      const { data } = await api.get(`/invoices/${iid}/share-link`);
      if (data?.url) window.open(data.url, "_blank", "noopener");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (handleAuthError(status, detail)) return;
      toast.error(typeof detail === "string" ? detail : "Could not open PDF");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-invoices-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Invoices</h1>
          <p className="text-sm text-slate-600 mt-1">Bank-transfer + Stripe invoices. Mark paid → unlocks the corresponding reservation / wallet credit. Create custom invoices for one-off billing.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500 inline-flex items-center gap-1.5">
            <span className="inline-block size-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live · {rows.length} invoices
          </span>
          <Button onClick={() => setCreateOpen(true)} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" size="sm" data-testid="create-invoice-btn">
            <Plus className="size-3.5" /> Create invoice
          </Button>
        </div>
      </header>

      {/* iter90p — Stale-unpaid action banner with dry-run "Nudge all" CTA. */}
      {/* Calm brand palette — soft white→slate→brand wash, no amber. */}
      {vaultStats.stale_unpaid > 0 && (
        <div
          className="rounded-xl border border-slate-200 bg-gradient-to-r from-white via-slate-50 to-[#0055FF]/[0.04] px-4 py-3 flex items-center gap-3"
          data-testid="invoices-stale-banner"
        >
          <span className="inline-grid place-items-center size-9 rounded-lg bg-[#0055FF]/10 text-[#0055FF] flex-shrink-0">
            <Clock className="size-4" strokeWidth={2.2} />
          </span>
          <button
            onClick={() => { setStatusFilter("issued"); setPmFilter("all"); }}
            className="flex-1 min-w-0 text-left group"
            data-testid="invoices-stale-banner-filter"
          >
            <div className="text-[13.5px] font-semibold text-slate-900 tracking-tight">
              {vaultStats.stale_unpaid} invoice{vaultStats.stale_unpaid === 1 ? "" : "s"} unpaid for over 14 days · £{Number(vaultStats.outstanding_gbp || 0).toLocaleString()} outstanding
            </div>
            <div className="text-[12px] text-slate-500">
              Filter to review them &middot; <span className="text-[#0055FF] group-hover:underline">Review &rarr;</span>
            </div>
          </button>
          <Button
            size="sm"
            onClick={openNudgePreview}
            disabled={nudgePreviewBusy}
            className="rounded-md h-9 bg-[#0055FF] hover:bg-[#0044CC] text-white gap-1.5 flex-shrink-0"
            data-testid="invoices-nudge-all"
          >
            {nudgePreviewBusy ? <Loader2 className="size-3.5 animate-spin" /> : <><Bell className="size-3.5" /> Nudge all</>}
          </Button>
        </div>
      )}

      {/* iter90p — Vault-wide KPI strip. Counts are independent of filters; */}
      {/* first three cards click to filter; the fourth shows £ outstanding. */}
      <div className="grid sm:grid-cols-4 gap-4">
        <KpiClickable
          Icon={FileText} label="Total invoices" value={vaultStats.total}
          active={statusFilter === "all" && pmFilter === "all"}
          activeLabel="All shown"
          onClick={() => { setStatusFilter("all"); setPmFilter("all"); }}
          testid="kpi-total"
        />
        <KpiClickable
          Icon={Clock} label="Awaiting payment" value={vaultStats.unpaid} tint="brand"
          active={statusFilter === "issued"}
          onClick={() => setStatusFilter(statusFilter === "issued" ? "all" : "issued")}
          testid="kpi-awaiting"
        />
        <KpiClickable
          Icon={CheckCircle2} label="Paid" value={vaultStats.paid} tint="emerald"
          active={statusFilter === "paid"}
          onClick={() => setStatusFilter(statusFilter === "paid" ? "all" : "paid")}
          testid="kpi-paid"
        />
        <KpiClickable
          Icon={Banknote} label="Outstanding £" value={`£${Number(vaultStats.outstanding_gbp || 0).toLocaleString()}`}
          tint="navy"
          testid="kpi-outstanding"
        />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-soft">
        <div className="p-4 flex flex-wrap items-center gap-2 border-b border-slate-200">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 size-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by invoice no, company, email…" className="pl-9" data-testid="invoices-search" />
          </div>
          <FilterPills label="Status" value={statusFilter} onChange={setStatusFilter} options={[
            ["all", "All"], ["issued", "Awaiting"], ["paid", "Paid"], ["overdue", "Overdue"],
          ]} testid="filter-status" />
          <FilterPills label="Method" value={pmFilter} onChange={setPmFilter} options={[
            ["all", "All"], ["card", "Card"], ["bank_transfer", "Bank"],
          ]} testid="filter-pm" />
          <div className="ml-auto"><ClearArea area="invoices" onDone={load} /></div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-3">Invoice</th>
                <th className="px-4 py-3">Buyer</th>
                <th className="px-4 py-3">Method</th>
                <th className="px-4 py-3">Issued</th>
                <th className="px-4 py-3">Due</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-12 text-center text-slate-400" data-testid="invoices-empty">No invoices match your filters.</td></tr>
              ) : filtered.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`invoice-row-${r.number || r.id?.slice(0,8)}`}>
                  <td className="px-4 py-3 font-mono text-[12.5px] text-slate-900">{r.number || r.id?.slice(0, 8)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{r.buyer?.company || r.buyer?.name || "—"}</div>
                    <div className="text-[11.5px] text-slate-500">{r.buyer?.email}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    <span className="inline-flex items-center gap-1.5 text-[12px]">
                      {r.payment_method === "bank_transfer" ? <Banknote className="size-3.5" /> : <CreditCard className="size-3.5" />}
                      {r.payment_method === "bank_transfer" ? "Bank" : "Card"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-700 text-[12.5px]">{fmt(r.issued_at)}</td>
                  <td className="px-4 py-3 text-slate-700 text-[12.5px]">{fmt(r.due_at)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-slate-900">£{Number(r.total_gbp || 0).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS_COLORS[r.status] || STATUS_COLORS.refunded}`}>
                      {r.status?.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setActive(r)} data-testid={`invoice-open-${r.number}`}>Details →</Button>
                      <RowBin area="invoices" id={r.id} name={`Invoice ${r.number || ""}`} onDone={load} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl" data-testid="invoice-detail-dialog">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="size-5 text-[#0055FF]" /> Invoice {active.number}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Status">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS_COLORS[active.status] || STATUS_COLORS.refunded}`}>{active.status?.replace(/_/g, " ")}</span>
                  </Field>
                  <Field label="Method">{active.payment_method === "bank_transfer" ? "Bank transfer" : "Card"}</Field>
                  <Field label="Buyer company">{active.buyer?.company || "—"}</Field>
                  <Field label="Buyer email">{active.buyer?.email}</Field>
                  <Field label="Issued">{fmt(active.issued_at)}</Field>
                  <Field label="Due">{fmt(active.due_at)}</Field>
                  <Field label="Kind">{active.kind?.replace(/_/g, " ")}</Field>
                  <Field label="Reservation ref">{active.reservation_id?.slice(0,8) || active.ai_order_id?.slice(0,8) || "—"}</Field>
                </div>
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-widest text-slate-500">
                        <tr><th className="px-3 py-2">Description</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Unit</th><th className="px-3 py-2 text-right">Amount</th></tr>
                      </thead>
                      <tbody>
                        {(active.line_items || []).map((li, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-3 py-2 text-slate-800">{li.description}</td>
                            <td className="px-3 py-2 text-right">{li.quantity}</td>
                            <td className="px-3 py-2 text-right">£{li.unit_price_gbp?.toLocaleString()}</td>
                            <td className="px-3 py-2 text-right font-mono">£{((li.quantity || 1) * (li.unit_price_gbp || 0)).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-slate-50 text-sm">
                        <tr><td colSpan="3" className="px-3 py-1.5 text-right text-slate-500">Subtotal</td><td className="px-3 py-1.5 text-right font-mono">£{Number(active.subtotal_gbp || 0).toLocaleString()}</td></tr>
                        <tr><td colSpan="3" className="px-3 py-1.5 text-right text-slate-500">VAT @ {Math.round((active.vat_rate || 0.2) * 100)}%</td><td className="px-3 py-1.5 text-right font-mono">£{Number(active.vat_gbp || 0).toLocaleString()}</td></tr>
                        <tr><td colSpan="3" className="px-3 py-2 text-right font-semibold text-slate-900">Total</td><td className="px-3 py-2 text-right font-mono font-semibold text-slate-900">£{Number(active.total_gbp || 0).toLocaleString()}</td></tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 justify-end pt-2">
                  <Button variant="outline" size="sm" onClick={() => downloadPdf(active.id)} className="rounded-full" data-testid="invoice-pdf">
                    <Download className="size-3.5" /> Download PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => resend(active.id)} className="rounded-full" data-testid="invoice-resend">
                    <Mail className="size-3.5" /> Resend
                  </Button>
                  {active.status !== "paid" && (
                    <Button size="sm" onClick={() => markPaid(active.id)} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="invoice-mark-paid">
                      <CheckCircle2 className="size-3.5" /> Mark paid
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <CreateInvoiceDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />

      {/* iter90p — Nudge-all dry-run preview + confirm dialog */}
      {nudgePreview && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-[0_30px_80px_rgba(15,23,42,0.25)] border border-slate-200 overflow-hidden max-h-[92vh] flex flex-col">
            <div className="px-6 py-4 border-b border-slate-100">
              <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#0055FF]">Confirm bulk send</div>
              <div className="text-lg font-semibold text-slate-900 tracking-tight mt-0.5">
                Nudge {nudgePreview.would_nudge} invoice{nudgePreview.would_nudge === 1 ? "" : "s"}?
              </div>
              <p className="text-[12.5px] text-slate-500 mt-1">
                {nudgePreview.would_skip > 0 && (
                  <>{nudgePreview.would_skip} more {nudgePreview.would_skip === 1 ? "is" : "are"} skipped (nudged in the last 24h). </>
                )}
                Each recipient is rate-limited to one nudge per 24 hours.
              </p>
            </div>

            <div className="px-6 py-4 flex-1 overflow-y-auto" data-testid="invoices-nudge-preview-list">
              {nudgePreview.preview && nudgePreview.preview.length > 0 ? (
                <>
                  <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">
                    Recipients ({nudgePreview.would_nudge}{nudgePreview.would_nudge > nudgePreview.preview.length ? ` · showing first ${nudgePreview.preview.length}` : ""})
                  </div>
                  <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <table className="w-full text-[12.5px]">
                      <thead className="bg-slate-50/60 text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
                        <tr>
                          <th className="text-left px-3 py-2">Invoice</th>
                          <th className="text-left px-3 py-2">Recipient</th>
                          <th className="text-right px-3 py-2">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {nudgePreview.preview.map((p, i) => (
                          <tr key={p.id} className="hover:bg-slate-50/40">
                            <td className="px-3 py-2 font-mono text-slate-700 text-[11.5px]">{p.number || p.id.slice(0, 12)}</td>
                            <td className="px-3 py-2 text-slate-900 truncate">
                              {p.email || <span className="text-slate-400 italic">(no email on file)</span>}
                              {p.company && <span className="text-slate-500"> · {p.company}</span>}
                            </td>
                            <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-900">{p.total_gbp != null ? `£${Number(p.total_gbp).toLocaleString()}` : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-[13px] text-slate-500 text-center py-6">
                  Nothing to nudge right now — every stale invoice has been nudged in the last 24 hours.
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between gap-3">
              <p className="text-[11.5px] text-slate-500 leading-tight">
                Clicking <b className="text-slate-700">Send</b> emails each recipient the original invoice PDF with bank-transfer details.
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setNudgePreview(null)} className="rounded-md h-10" disabled={nudgeSending} data-testid="nudge-cancel">Cancel</Button>
                <Button
                  onClick={confirmNudgeAll}
                  disabled={nudgeSending || !nudgePreview.would_nudge}
                  className="rounded-md h-10 bg-[#0055FF] hover:bg-[#0044CC] text-white min-w-[160px] gap-1.5"
                  data-testid="nudge-confirm"
                >
                  {nudgeSending ? <Loader2 className="size-4 animate-spin" /> : <><Bell className="size-3.5" /> Send {nudgePreview.would_nudge || ""} nudge{nudgePreview.would_nudge === 1 ? "" : "s"}</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Create custom invoice dialog
   ============================================================ */
function CreateInvoiceDialog({ open, onOpenChange, onCreated }) {
  const blankItem = { description: "", quantity: 1, unit_price_gbp: 0 };
  const [form, setForm] = useState({
    buyer_email: "", buyer_name: "", buyer_company: "",
    payment_method: "bank_transfer", due_days: 0, notes: "",
    send_email: true,
    line_items: [{ ...blankItem }],
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm({
        buyer_email: "", buyer_name: "", buyer_company: "",
        payment_method: "bank_transfer", due_days: 0, notes: "",
        send_email: true,
        line_items: [{ ...blankItem }],
      });
    }
  }, [open]);

  const subtotal = useMemo(
    () => form.line_items.reduce((s, li) => s + (Number(li.quantity) || 0) * (Number(li.unit_price_gbp) || 0), 0),
    [form.line_items],
  );
  const vat = +(subtotal * VAT_RATE).toFixed(2);
  const total = +(subtotal + vat).toFixed(2);

  const updateItem = (i, patch) => {
    setForm((f) => ({ ...f, line_items: f.line_items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
  };
  const addItem = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { ...blankItem }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, line_items: f.line_items.length > 1 ? f.line_items.filter((_, idx) => idx !== i) : f.line_items }));

  const submit = async () => {
    if (!form.buyer_email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) { toast.error("Valid buyer email required"); return; }
    if (form.line_items.some((li) => !li.description.trim() || li.unit_price_gbp <= 0)) { toast.error("Every line item needs a description + amount > 0"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invoices", {
        buyer_email: form.buyer_email.toLowerCase().trim(),
        buyer_name: form.buyer_name.trim(),
        buyer_company: form.buyer_company.trim(),
        line_items: form.line_items.map((li) => ({
          description: li.description.trim(),
          quantity: parseInt(li.quantity, 10) || 1,
          unit_price_gbp: Number(li.unit_price_gbp),
        })),
        kind: "custom",
        payment_method: form.payment_method,
        due_days: parseInt(form.due_days, 10) || 0,
        notes: form.notes.trim() || null,
        send_email: form.send_email,
      });
      toast.success(`Invoice ${data.invoice.number} created${data.emailed ? " · emailed" : ""}`);
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create invoice");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="create-invoice-dialog">
        <DialogHeader>
          <DialogTitle>Create custom invoice</DialogTitle>
          <DialogDescription>One-off invoice for any service · 20% VAT added automatically · branded PDF + bank-transfer details included.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Buyer email *"><Input value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} placeholder="client@company.co.uk" data-testid="ci-email" /></Field>
            <Field label="Buyer name"><Input value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} placeholder="Jane Doe" data-testid="ci-name" /></Field>
            <Field label="Company"><Input value={form.buyer_company} onChange={(e) => setForm({ ...form, buyer_company: e.target.value })} placeholder="Acme Ltd" data-testid="ci-company" /></Field>
          </div>

          <div>
            <Label className="text-xs font-medium text-slate-700 mb-2 block">Line items</Label>
            <div className="space-y-2">
              {form.line_items.map((li, i) => (
                <div key={i} className="grid grid-cols-[1fr_60px_100px_30px] gap-2 items-center" data-testid={`ci-line-${i}`}>
                  <Input value={li.description} onChange={(e) => updateItem(i, { description: e.target.value })} placeholder="e.g. AI Voice Agent setup · custom build" />
                  <Input type="number" min={1} value={li.quantity} onChange={(e) => updateItem(i, { quantity: e.target.value })} className="text-center tabular-nums" />
                  <Input type="number" min={0} step={0.01} value={li.unit_price_gbp} onChange={(e) => updateItem(i, { unit_price_gbp: e.target.value })} placeholder="£" className="text-right tabular-nums" />
                  <Button variant="ghost" size="sm" onClick={() => removeItem(i)} className="size-9 p-0 text-slate-400 hover:text-rose-600" disabled={form.line_items.length === 1}><Trash2 className="size-3.5" /></Button>
                </div>
              ))}
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={addItem} className="mt-2 text-[#0055FF] hover:text-[#0044CC] rounded-full" data-testid="ci-add-line"><Plus className="size-3.5" /> Add line item</Button>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Payment method">
              <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option value="bank_transfer">Bank transfer</option>
                <option value="card">Card</option>
              </select>
            </Field>
            <Field label="Payment terms (days)">
              <select value={form.due_days} onChange={(e) => setForm({ ...form, due_days: parseInt(e.target.value, 10) })} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="ci-due-days">
                <option value={0}>Due on receipt</option>
                <option value={7}>Net 7</option>
                <option value={14}>Net 14</option>
                <option value={30}>Net 30</option>
              </select>
            </Field>
            <Field label="Email invoice on create">
              <label className="h-10 flex items-center gap-2 rounded-md border border-slate-200 px-3 cursor-pointer hover:border-slate-300">
                <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} className="size-4" />
                <span className="text-sm text-slate-700">Send PDF to buyer</span>
              </label>
            </Field>
          </div>

          <Field label="Internal notes (optional · shown on PDF)">
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Q1 2026 — agreed package per call 14/02/2026" data-testid="ci-notes" />
          </Field>

          {/* Live totals */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-mono tabular-nums">£{subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">VAT @ 20%</span><span className="font-mono tabular-nums">£{vat.toFixed(2)}</span></div>
            <div className="border-t border-slate-200 pt-1.5 flex justify-between font-semibold"><span className="text-slate-700">Total</span><span className="font-mono tabular-nums text-base">£{total.toFixed(2)}</span></div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="ci-submit">
            {busy ? <><Loader2 className="size-4 animate-spin" /> Creating…</> : <>Create invoice · £{total.toFixed(2)} <CheckCircle2 className="size-4" /></>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KpiClickable({ Icon, label, value, tint = "slate", active = false, onClick, testid, activeLabel = "Filtered" }) {
  // iter90p — calm brand palette replaces amber. `brand` = #0055FF, `navy` = #001A4A.
  const map = {
    slate:   { icon: "text-slate-700",     value: "text-slate-900",   ring: "ring-slate-300",    bgActive: "bg-slate-50" },
    brand:   { icon: "text-[#0055FF]",     value: "text-[#0055FF]",   ring: "ring-[#0055FF]/30", bgActive: "bg-[#0055FF]/[0.05]" },
    emerald: { icon: "text-emerald-600",   value: "text-emerald-600", ring: "ring-emerald-300",  bgActive: "bg-emerald-50" },
    navy:    { icon: "text-[#001A4A]",     value: "text-[#001A4A]",   ring: "ring-slate-400",    bgActive: "bg-slate-50" },
  }[tint] || { icon: "text-slate-700", value: "text-slate-900", ring: "ring-slate-300", bgActive: "bg-slate-50" };
  const clickable = !!onClick;
  const Component = clickable ? "button" : "div";
  return (
    <Component
      onClick={onClick}
      className={`rounded-xl border bg-white p-5 shadow-soft text-left transition-all ${
        clickable ? `hover:ring-2 hover:${map.ring} hover:-translate-y-0.5 active:translate-y-0` : ""
      } ${active ? `ring-2 ${map.ring} ${map.bgActive} border-transparent` : "border-slate-200"}`}
      data-testid={testid}
    >
      <Icon className={`size-4 ${map.icon}`} strokeWidth={1.7} />
      <div className={`mt-3 text-2xl font-semibold tracking-tight tabular-nums ${map.value}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-1">{label}</div>
      {clickable && (
        <div className="text-[10.5px] text-slate-400 mt-1.5 uppercase tracking-[0.14em] font-mono">
          {active ? activeLabel : "Click to filter"}
        </div>
      )}
    </Component>
  );
}

function FilterPills({ label, value, onChange, options, testid }) {
  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      <span className="text-[11px] uppercase tracking-widest text-slate-500 inline-flex items-center gap-1"><Filter className="size-3" /> {label}</span>
      {options.map(([v, l]) => (
        <button key={v} onClick={() => onChange(v)} className={`text-[12px] rounded-full px-2.5 py-1 transition-colors ${value === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 text-slate-900 text-[13px]">{children}</div>
    </div>
  );
}

function fmt(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s.slice(0, 10); }
}
